import { memo, useCallback, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import SkeletonText from 'conversations-skeleton-state/components/SkeletonText';
import SkeletonView from 'conversations-skeleton-state/components/SkeletonView';
import ErrorMessageWithRetry from '../../shared/components/ErrorMessageWithRetry';
import { useIsUngatedForUserLevelCapacityLimitsTicketType } from '../../auth/hooks/useGates';
import { useOutOfOfficeHours } from '../../working-hours/hooks/useOutOfOfficeHours';
import { TrackUsageProvider } from '../../usage-tracking/contexts/TrackUsageContext';
import { AvailabilityButton } from './AvailabilityPopover/AvailabilityButton';
import AgentStatusPrompt from './AgentStatusPrompt';
import AgentStatusToggle from './AgentStatusToggle';
import { CapacityLimits } from './CapacityLimits';
import { EndOutOfOfficePrompt } from './EndOutOfOfficePrompt';
import { StatusMessage } from './StatusMessage/StatusMessage';
import { useAgentStatus } from '../hooks/useAgentStatus';
import { useRealtimeAgentStatus } from '../hooks/useRealtimeAgentStatus';
import { getAwayMode } from '../operators/getters';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useAwayStatusTimer } from '../../working-hours/hooks/useAwayStatusTimer';
import { useAgentRemainingAwayMinutes } from '../hooks/useAgentRemainingAwayMinutes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AgentStatus = ({
  agentId,
  atCapacity,
  portalCapacityLimit,
  portalCapacityLoad,
  Prompt,
  showUserLevelCapacityLimits = false,
  usageTracker,
  workspace
}) => {
  const isUngatedForUserLevelCapacityLimitsTicketType = useIsUngatedForUserLevelCapacityLimitsTicketType();
  const {
    agentStatus,
    error,
    loadingAgentStatus,
    refetchAgentStatus,
    toggleAwayMode: updateAwayMode,
    updateAwayModeError
  } = useAgentStatus(usageTracker, workspace);
  useRealtimeAgentStatus(agentId, workspace);
  const awayModeState = agentStatus && getAwayMode(agentStatus);
  const [showConfirmEndOutOfOffice, setShowConfirmEndOutOfOffice] = useState(false);
  const {
    currentSegment,
    isLoadingOOOHours
  } = useOutOfOfficeHours(agentId, {
    isViewingSelf: true
  });
  const {
    awayStatusTimer
  } = useAwayStatusTimer(agentId, {
    isViewingSelf: true
  });
  const remainingMins = useAgentRemainingAwayMinutes(awayStatusTimer);
  const handleAwayModeUpdate = useCallback(awayMode => {
    if (currentSegment && !awayMode) {
      setShowConfirmEndOutOfOffice(true);
    } else {
      updateAwayMode(awayMode);
    }
  }, [currentSegment, updateAwayMode]);
  if (loadingAgentStatus || isLoadingOOOHours) {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      inlineSize: "100%",
      justifyContent: "space-between",
      paddingInlineStart: "200",
      children: [/*#__PURE__*/_jsx(SkeletonText, {
        inlineSize: "100px"
      }), /*#__PURE__*/_jsx(SkeletonView, {
        blockSize: "26px",
        inlineSize: "52px"
      })]
    });
  }
  if (error || !agentStatus) {
    // Only degrade for errors critical to the function of agent status
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.loadError"
      }),
      retry: refetchAgentStatus
    });
  }
  if (updateAwayModeError) {
    return /*#__PURE__*/_jsx(ErrorMessageWithRetry, {
      title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "routing-ui.agent-status.updateError"
      }),
      retry: () => updateAwayMode(!awayModeState)
    });
  }
  const renderAgentAvailabilityControls = () => {
    if (isUngatedForUserLevelCapacityLimitsTicketType) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(AvailabilityButton, {
          isAway: !!awayModeState,
          portalCapacityLimit: portalCapacityLimit,
          portalCapacityLoad: portalCapacityLoad,
          updateAwayMode: handleAwayModeUpdate,
          userId: agentId,
          workspace: workspace
        }), remainingMins > 0 && /*#__PURE__*/_jsx(Flex, {
          paddingInlineStart: "700",
          alignItems: "center",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "routing-ui.agent-status.timerStatus.description",
            options: {
              count: remainingMins
            }
          })
        }), /*#__PURE__*/_jsx(StatusMessage, {
          agentId: agentId,
          awayMode: !!awayModeState,
          portalCapacityLimit: portalCapacityLimit,
          portalCapacityLoad: portalCapacityLoad,
          workspace: workspace
        })]
      });
    }
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AgentStatusToggle, {
        agentStatus: agentStatus,
        atCapacity: atCapacity,
        currentSegment: currentSegment,
        updateAwayMode: handleAwayModeUpdate,
        userId: agentId
      }), showUserLevelCapacityLimits && /*#__PURE__*/_jsx(CapacityLimits, {
        agentId: agentId
      })]
    });
  };
  return /*#__PURE__*/_jsxs(TrackUsageProvider, {
    tracker: usageTracker,
    children: [showConfirmEndOutOfOffice && /*#__PURE__*/_jsx(EndOutOfOfficePrompt, {
      userId: agentId,
      isViewingSelf: true,
      onConfirm: () => {
        updateAwayMode(false);
        setShowConfirmEndOutOfOffice(false);
      },
      onReject: () => setShowConfirmEndOutOfOffice(false)
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(AgentStatusPrompt, {
        agentStatus: agentStatus,
        Prompt: Prompt,
        children: renderAgentAvailabilityControls()
      })
    })]
  });
};
AgentStatus.displayName = 'AgentStatus';
export default /*#__PURE__*/memo(AgentStatus);